import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import api from '../services/api';
import { useToast } from '../hooks/useToast';

const الإشعارات = () => {
  const [activeView, setActiveView] = useState('السجل');
  const [notifications, setNotifications] = useState([]);
  const [allNotifications, setAllNotifications] = useState([]); // ✅ جميع الإشعارات من الـ API
  const [groupedNotifications, setGroupedNotifications] = useState([]); // ✅ جميع الإشعارات المجمعة
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [expandedNotifications, setExpandedNotifications] = useState(new Set());
  const { showToast, ToastContainer } = useToast();

  const [sendForm, setSendForm] = useState({
    title: '',
    message: '',
    target: 'all'
  });

  useEffect(() => {
    if (activeView === 'السجل') {
      fetchAllNotifications();
    }
  }, [activeView]);

  // ✅ إعادة حساب الـ pagination عند تغيير الصفحة (لا حاجة لجلب البيانات مرة أخرى)
  useEffect(() => {
    if (groupedNotifications.length > 0) {
      const groupedTotalPages = Math.ceil(groupedNotifications.length / 10);
      setTotalPages(groupedTotalPages || 1);
    }
  }, [groupedNotifications, page]);

  // ✅ جلب جميع الإشعارات مرة واحدة
  const fetchAllNotifications = async (showLoading = false) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      
      // ✅ جلب جميع الإشعارات بشكل متكرر إذا كان العدد أكبر من 1000
      let allItems = [];
      let currentPage = 1;
      const limit = 1000;
      let hasMore = true;
      
      while (hasMore) {
        const data = await api.getNotifications(currentPage, limit);
        if (data && data.items && data.items.length > 0) {
          allItems = [...allItems, ...data.items];
          // إذا كان عدد الإشعارات أقل من limit، يعني وصلنا للنهاية
          if (data.items.length < limit || (data.totalPages && currentPage >= data.totalPages)) {
            hasMore = false;
          } else {
            currentPage++;
          }
        } else {
          hasMore = false;
        }
      }
      
      if (allItems.length > 0) {
        setAllNotifications(allItems);
        setTotal(allItems.length);
        
        // تجميع جميع الإشعارات
        const grouped = processNotifications(allItems);
        setGroupedNotifications(grouped);
        
        // حساب عدد الصفحات بناءً على الإشعارات المجمعة (10 لكل صفحة)
        const groupedTotalPages = Math.ceil(grouped.length / 10);
        setTotalPages(groupedTotalPages || 1);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
      showToast('حدث خطأ في جلب الإشعارات', 'error');
    } finally {
      setLoading(false);
    }
  };

  // ✅ دالة لجلب الإشعارات (للتوافق مع الكود القديم)
  const fetchNotifications = async (showLoading = false) => {
    await fetchAllNotifications(showLoading);
  };

  const handleSendNotification = async () => {
    if (!sendForm.title.trim() || !sendForm.message.trim()) {
      showToast('يرجى إدخال العنوان والرسالة', 'error');
      return;
    }

    setSending(true);
    try {
      await api.sendNotification(sendForm.title.trim(), sendForm.message.trim(), sendForm.target);
      showToast('تم إرسال الإشعار بنجاح', 'success');
      setSendForm({ title: '', message: '', target: 'all' });
      setActiveView('السجل');
      setPage(1);
      setTimeout(() => {
        fetchNotifications(true);
      }, 100);
    } catch (error) {
      console.error('Error sending notification:', error);
      showToast(error.message || 'حدث خطأ في إرسال الإشعار', 'error');
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    const localDate = new Date(date.getTime() - (date.getTimezoneOffset() * 60000));

    if (diffMins < 1) {
      return 'الآن';
    } else if (diffMins < 60) {
      return `منذ ${diffMins} دقيقة`;
    } else if (diffHours < 24) {
      return `منذ ${diffHours} ساعة`;
    } else if (diffDays === 1) {
      return 'أمس';
    } else if (diffDays < 7) {
      return `منذ ${diffDays} أيام`;
    } else {
      const year = localDate.getFullYear();
      const month = String(localDate.getMonth() + 1).padStart(2, '0');
      const day = String(localDate.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const localDate = new Date(date.getTime() - (date.getTimezoneOffset() * 60000));
    const hours = String(localDate.getHours()).padStart(2, '0');
    const minutes = String(localDate.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  // تجميع الإشعارات الجماعية فقط (التي أُرسلت للجميع)
  const processNotifications = (notifications) => {
    const grouped = {};
    const individual = [];
    
    // دالة لتنظيف النص من المسافات الزائدة والأحرف الخاصة
    const cleanText = (text) => {
      if (!text) return '';
      return String(text)
        .trim()
        .replace(/\s+/g, ' ') // استبدال جميع المسافات المتعددة بمسافة واحدة
        .replace(/\n+/g, ' ') // استبدال الأسطر الجديدة بمسافة
        .replace(/\r+/g, ' ') // استبدال carriage return بمسافة
        .replace(/\t+/g, ' ') // استبدال tabs بمسافة
        .trim();
    };
    
    notifications.forEach((notification) => {
      // ✅ استخراج target من notification.target مباشرة (الآن متاح من الـ backend)
      // أو من data إذا كان target غير متاح مباشرة (للتوافق مع البيانات القديمة)
      let target = null;
      
      // محاولة 1: من notification.target مباشرة
      if (notification.target) {
        target = String(notification.target).trim().toLowerCase();
      }
      
      // محاولة 2: من notification.data (object أو string)
      if (!target && notification.data) {
        let dataObj = null;
        
        // إذا كان object مباشرة
        if (typeof notification.data === 'object' && notification.data !== null) {
          dataObj = notification.data;
        }
        // إذا كان string JSON
        else if (typeof notification.data === 'string' && notification.data.trim()) {
          try {
            dataObj = JSON.parse(notification.data);
          } catch (e) {
            // إذا فشل التحويل، نحاول استخدام data كـ string مباشرة
            console.warn('Failed to parse notification.data as JSON:', e);
          }
        }
        
        // استخراج target من dataObj
        if (dataObj && typeof dataObj === 'object' && dataObj !== null) {
          // محاولة الوصول إلى target بطرق مختلفة
          target = dataObj.target || dataObj?.target || dataObj['target'] || null;
          if (target) {
            target = String(target).trim().toLowerCase();
          }
        }
      }
      
      // ✅ تطبيع target للقيم المتشابهة (store -> stores, user -> users, kitchen -> kitchens)
      if (target === 'store') target = 'stores';
      if (target === 'user') target = 'users';
      if (target === 'kitchen') target = 'kitchens';
      
      // ✅ Debug: طباعة معلومات target للتحقق (لجميع الإشعارات الجماعية)
      if (notification.title && (notification.target || notification.data)) {
        const isGroupTarget = ['all', 'users', 'stores', 'kitchens'].includes(target || '');
        if (isGroupTarget && !target) {
          console.error('❌ ERROR: Group notification without target!', {
            id: notification.id,
            title: notification.title?.substring(0, 30),
            notificationTarget: notification.target,
            data: notification.data,
            dataType: typeof notification.data,
            parsedData: typeof notification.data === 'string' ? (() => {
              try { return JSON.parse(notification.data); } catch { return null; }
            })() : notification.data
          });
        }
      }
      
      // تحديد نوع الإشعار: جماعي أو فردي
      // الإشعار جماعي إذا كان له target يدل على إرسال جماعي (بغض النظر عن وجود user)
      const isGroupNotification = target && ['all', 'users', 'stores', 'kitchens'].includes(target);
      
      // إذا كان الإشعار جماعي - يجب تجميعه (حتى لو كان له user)
      if (isGroupNotification) {
        // تنظيف العنوان والرسالة
        const cleanTitle = cleanText(notification.title || '');
        const cleanMessage = cleanText(notification.message || '');
        
        // ✅ تجميع جميع الإشعارات الجماعية المتطابقة (بنفس العنوان والرسالة و target)
        // ✅ المفتاح: العنوان + الرسالة + target لضمان تجميع الإشعارات المتطابقة فقط
        // هذا يضمن تجميع جميع الإشعارات الجماعية المتشابهة بغض النظر عن الوقت
        const key = `${cleanTitle}_${cleanMessage}_${target || 'unknown'}`;
        
        // ✅ Debug: طباعة معلومات التجميع (يمكن حذفها بعد التحقق)
        if (target === 'stores' || target === 'users' || target === 'kitchens') {
          console.log(`🔔 ${target.toUpperCase()} Notification Debug:`, {
            target,
            originalTarget: notification.target,
            data: notification.data,
            title: cleanTitle.substring(0, 30),
            message: cleanMessage.substring(0, 30),
            key: key.substring(0, 100),
            hasGroup: !!grouped[key],
            currentCount: grouped[key]?.recipientCount || 0,
            notificationId: notification.id,
            isGroupNotification,
            allKeys: Object.keys(grouped).filter(k => k.includes(cleanTitle.substring(0, 20))).slice(0, 5)
          });
        }
        
        if (!grouped[key]) {
          grouped[key] = {
            id: `group_${key}`,
            title: notification.title,
            message: notification.message,
            createdAt: notification.createdAt || notification.date,
            date: notification.date,
            read: notification.read,
            target: target,
            recipientCount: 1,
            readCount: notification.read ? 1 : 0,
            unreadCount: notification.read ? 0 : 1,
            isGrouped: true,
            allNotifications: [notification]
          };
        } else {
          grouped[key].recipientCount += 1;
          grouped[key].allNotifications.push(notification);
          // حساب عدد المقروء وغير المقروء
          if (notification.read) {
            grouped[key].readCount += 1;
          } else {
            grouped[key].unreadCount += 1;
            grouped[key].read = false;
          }
          // احتفظ بأحدث تاريخ
          const currentDate = new Date(grouped[key].createdAt || grouped[key].date);
          const newDate = new Date(notification.createdAt || notification.date);
          if (newDate > currentDate) {
            grouped[key].createdAt = notification.createdAt || notification.date;
            grouped[key].date = notification.date;
          }
        }
      } else {
        // إشعارات فردية (ليس لها target جماعي) - نعرضها منفصلة
        individual.push(notification);
      }
    });
    
    // دمج الإشعارات المجمعة والفردية
    const allNotifications = [...Object.values(grouped), ...individual];
    
    // ترتيب: غير المقروءة أولاً، ثم حسب التاريخ
    return allNotifications.sort((a, b) => {
      if (a.read !== b.read) {
        return a.read ? 1 : -1;
      }
      const dateA = new Date(a.createdAt || a.date || 0);
      const dateB = new Date(b.createdAt || b.date || 0);
      return dateB - dateA;
    });
  };

  // ✅ تقسيم الإشعارات المجمعة إلى صفحات (10 لكل صفحة)
  const itemsPerPage = 10;
  const startIndex = (page - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedNotifications = groupedNotifications.slice(startIndex, endIndex);
  
  // ✅ استخدام الإشعارات المجمعة والمقسمة للعرض
  const sortedNotifications = paginatedNotifications;

  const toggleNotification = (notificationId) => {
    const newExpanded = new Set(expandedNotifications);
    if (newExpanded.has(notificationId)) {
      newExpanded.delete(notificationId);
    } else {
      newExpanded.add(notificationId);
    }
    setExpandedNotifications(newExpanded);
  };

  return (
    <div style={{ 
      width: '100%', 
      height: '100vh', 
      display: 'flex', 
      flexDirection: 'column',
      backgroundColor: '#F8F9FA',
      overflow: 'hidden',
      direction: 'rtl'
    }}>
      <Header />
      <div style={{ 
        flex: 1, 
        padding: '30px 40px', 
        backgroundColor: '#F8F9FA', 
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        boxSizing: 'border-box'
      }}>
        {/* Header Section */}
        <div style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '4px'
        }}>
          <h2 style={{ 
            fontFamily: 'El Messiri, sans-serif', 
            fontSize: '32px', 
            fontWeight: 700, 
            color: '#1F2937',
            margin: 0,
            background: 'linear-gradient(135deg, #E96A3A 0%, #F27D72 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            الإشعارات
          </h2>
          
          <div style={{ 
            display: 'flex', 
            flexDirection: 'row',
            gap: '6px',
            backgroundColor: '#FFFFFF',
            padding: '6px',
            borderRadius: '14px',
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.08)',
            border: '1px solid #E5E7EB'
          }}>
            <button
              onClick={() => setActiveView('السجل')}
              style={{
                padding: '12px 28px',
                borderRadius: '10px',
                border: 'none',
                backgroundColor: activeView === 'السجل' ? '#E96A3A' : 'transparent',
                color: activeView === 'السجل' ? '#FFFFFF' : '#6B7280',
                fontFamily: 'Cairo, sans-serif',
                fontSize: '15px',
                fontWeight: activeView === 'السجل' ? 600 : 500,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: activeView === 'السجل' ? '0px 2px 8px rgba(233, 106, 58, 0.3)' : 'none'
              }}
            >
              السجل
            </button>
            <button
              onClick={() => setActiveView('إرسال إشعار')}
              style={{
                padding: '12px 28px',
                borderRadius: '10px',
                border: 'none',
                backgroundColor: activeView === 'إرسال إشعار' ? '#E96A3A' : 'transparent',
                color: activeView === 'إرسال إشعار' ? '#FFFFFF' : '#6B7280',
                fontFamily: 'Cairo, sans-serif',
                fontSize: '15px',
                fontWeight: activeView === 'إرسال إشعار' ? 600 : 500,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: activeView === 'إرسال إشعار' ? '0px 2px 8px rgba(233, 106, 58, 0.3)' : 'none'
              }}
            >
              إرسال إشعار
            </button>
          </div>
        </div>

        {activeView === 'السجل' ? (
          /* Notifications List */
          <div style={{ 
            backgroundColor: '#FFFFFF', 
            borderRadius: '20px', 
            padding: '28px',
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.06)',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            border: '1px solid #E5E7EB'
          }}>
            {loading && notifications.length === 0 ? (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '80px 20px',
                color: '#6B7280',
                fontFamily: 'Cairo, sans-serif'
              }}>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '20px'
                }}>
                  <div style={{
                    width: '56px',
                    height: '56px',
                    border: '5px solid rgba(233, 106, 58, 0.15)',
                    borderTop: '5px solid #E96A3A',
                    borderRadius: '50%',
                    animation: 'spin 0.8s linear infinite'
                  }}></div>
                  <span style={{ fontSize: '17px', fontWeight: 500 }}>جاري التحميل...</span>
                </div>
              </div>
            ) : sortedNotifications.length === 0 ? (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '80px 20px',
                color: '#6B7280',
                fontFamily: 'Cairo, sans-serif',
                fontSize: '17px',
                fontWeight: 500
              }}>
                لا توجد إشعارات
              </div>
            ) : (
              <>
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: '10px',
                  flex: 1,
                  overflow: 'auto',
                  paddingBottom: '24px',
                  position: 'relative'
                }}>
                  {loading && (
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'flex-start',
                      paddingTop: '50px',
                      zIndex: 10,
                      borderRadius: '20px',
                      backdropFilter: 'blur(2px)'
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '14px',
                        color: '#E96A3A',
                        fontFamily: 'Cairo, sans-serif',
                        fontSize: '15px',
                        fontWeight: 500
                      }}>
                        <div style={{
                          width: '28px',
                          height: '28px',
                          border: '3px solid rgba(233, 106, 58, 0.2)',
                          borderTop: '3px solid #E96A3A',
                          borderRadius: '50%',
                          animation: 'spin 0.6s linear infinite'
                        }}></div>
                        جاري التحديث...
                      </div>
                    </div>
                  )}
                  
                  {sortedNotifications.map((notification, index) => {
                    const isExpanded = expandedNotifications.has(notification.id || index);
                    const messagePreview = notification.message.length > 80 
                      ? notification.message.substring(0, 80) + '...' 
                      : notification.message;
                    
                    return (
                      <div
                        key={notification.id || index}
                        style={{
                          padding: '12px 16px',
                          borderRadius: '10px',
                          backgroundColor: notification.read ? '#FFFFFF' : '#FFFBF9',
                          border: notification.read 
                            ? '1px solid #E5E7EB' 
                            : '1px solid #E96A3A',
                          borderRight: notification.read ? '1px solid #E5E7EB' : '3px solid #E96A3A',
                          boxShadow: notification.read 
                            ? '0px 1px 3px rgba(0, 0, 0, 0.05)' 
                            : '0px 2px 6px rgba(233, 106, 58, 0.15)',
                          transition: 'all 0.2s ease',
                          position: 'relative',
                          cursor: 'pointer'
                        }}
                        onClick={() => toggleNotification(notification.id || index)}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.boxShadow = notification.read 
                            ? '0px 2px 8px rgba(0, 0, 0, 0.08)' 
                            : '0px 4px 12px rgba(233, 106, 58, 0.25)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.boxShadow = notification.read 
                            ? '0px 1px 3px rgba(0, 0, 0, 0.05)' 
                            : '0px 2px 6px rgba(233, 106, 58, 0.15)';
                        }}
                      >
                        {/* Badge for unread */}
                        {!notification.read && (
                          <div style={{
                            position: 'absolute',
                            top: '12px',
                            left: '12px',
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: '#E96A3A',
                            boxShadow: '0 0 8px rgba(233, 106, 58, 0.8)'
                          }}></div>
                        )}
                        
                        {/* Compact Header */}
                        <div style={{ 
                          display: 'flex', 
                          flexDirection: 'row',
                          justifyContent: 'space-between', 
                          alignItems: 'center', 
                          gap: '12px',
                          marginBottom: isExpanded ? '10px' : '0'
                        }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              marginBottom: '4px'
                            }}>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <h4 style={{ 
                                  fontFamily: 'El Messiri, sans-serif', 
                                  fontSize: '15px', 
                                  fontWeight: 600, 
                                  color: '#1F2937',
                                  margin: 0,
                                  textAlign: 'right',
                                  lineHeight: '1.4',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}>
                                  {notification.title}
                                </h4>
                                {notification.isGrouped && (
                                  <div style={{
                                    marginTop: '2px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    padding: '2px 8px',
                                    backgroundColor: '#FEF3F2',
                                    borderRadius: '6px',
                                    fontFamily: 'Cairo, sans-serif',
                                    fontSize: '10px',
                                    color: '#E96A3A',
                                    fontWeight: 600
                                  }}>
                                    <span>📢</span>
                                    <span>
                                      {notification.target === 'all' ? 'إرسال جماعي - جميع المستخدمين' :
                                       notification.target === 'users' ? 'إرسال جماعي - المستخدمين فقط' :
                                       notification.target === 'stores' ? 'إرسال جماعي - أصحاب المتاجر' :
                                       notification.target === 'kitchens' ? 'إرسال جماعي - أصحاب المطابخ' :
                                       'إرسال جماعي'}
                                    </span>
                                  </div>
                                )}
                              </div>
                              {notification.isGrouped && (
                                <span style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  minWidth: '28px',
                                  height: '28px',
                                  padding: '0 8px',
                                  borderRadius: '14px',
                                  backgroundColor: '#E96A3A',
                                  color: '#FFFFFF',
                                  fontFamily: 'Cairo, sans-serif',
                                  fontSize: '12px',
                                  fontWeight: 700,
                                  flexShrink: 0,
                                  boxShadow: '0 2px 4px rgba(233, 106, 58, 0.3)'
                                }}>
                                  {notification.recipientCount}
                                </span>
                              )}
                            </div>
                            {!isExpanded && (
                              <p style={{ 
                                fontFamily: 'Cairo, sans-serif', 
                                fontSize: '13px', 
                                color: '#6B7280',
                                margin: 0,
                                textAlign: 'right',
                                lineHeight: '1.5',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}>
                                {messagePreview}
                              </p>
                            )}
                          </div>
                          
                          <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-end',
                            gap: '2px',
                            flexShrink: 0,
                            paddingRight: '8px'
                          }}>
                            <span style={{
                              fontFamily: 'Cairo, sans-serif',
                              fontSize: '11px',
                              color: notification.read ? '#9CA3AF' : '#E96A3A',
                              fontWeight: 500
                            }}>
                              {formatDate(notification.createdAt || notification.date)}
                            </span>
                            <span style={{
                              fontFamily: 'Cairo, sans-serif',
                              fontSize: '10px',
                              color: '#9CA3AF',
                              fontWeight: 400
                            }}>
                              {formatTime(notification.createdAt || notification.date)}
                            </span>
                          </div>
                        </div>
                        
                        {/* Expanded Details */}
                        {isExpanded && (
                          <div style={{
                            marginTop: '10px',
                            paddingTop: '10px',
                            borderTop: '1px solid #E5E7EB',
                            animation: 'slideDown 0.2s ease'
                          }}>
                            {/* Full Message */}
                            <p style={{ 
                              fontFamily: 'Cairo, sans-serif', 
                              fontSize: '13px', 
                              color: '#374151',
                              margin: '0 0 10px 0',
                              textAlign: 'right',
                              lineHeight: '1.6',
                              padding: '10px 12px',
                              backgroundColor: notification.read ? '#F9FAFB' : '#FFFBF9',
                              borderRadius: '8px',
                              border: `1px solid ${notification.read ? '#E5E7EB' : '#FFE4D9'}`
                            }}>
                              {notification.message}
                            </p>
                            
                            {/* Target Info */}
                            {notification.target && (
                              <div style={{
                                padding: '6px 10px',
                                backgroundColor: '#F3F4F6',
                                borderRadius: '6px',
                                fontFamily: 'Cairo, sans-serif',
                                fontSize: '12px',
                                color: '#4B5563',
                                display: 'inline-block',
                                border: '1px solid #E5E7EB',
                                marginBottom: '8px'
                              }}>
                                <strong style={{ color: '#1F2937', fontWeight: 600 }}>إلى:</strong> {
                                  notification.target === 'all' ? 'جميع المستخدمين' :
                                  notification.target === 'users' ? 'المستخدمين فقط' :
                                  notification.target === 'stores' ? 'أصحاب المتاجر' :
                                  notification.target === 'kitchens' ? 'أصحاب المطابخ' :
                                  notification.target
                                }
                              </div>
                            )}
                            
                            {/* إحصائيات الإشعارات المجمعة */}
                            {notification.isGrouped && (
                              <div style={{
                                marginTop: '12px',
                                padding: '12px',
                                backgroundColor: '#F9FAFB',
                                borderRadius: '8px',
                                border: '1px solid #E5E7EB',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '8px'
                              }}>
                                <div style={{
                                  fontFamily: 'Cairo, sans-serif',
                                  fontSize: '13px',
                                  fontWeight: 600,
                                  color: '#1F2937',
                                  marginBottom: '4px'
                                }}>
                                  إحصائيات الإشعار:
                                </div>
                                <div style={{
                                  display: 'flex',
                                  flexDirection: 'row',
                                  flexWrap: 'wrap',
                                  gap: '8px'
                                }}>
                                  <div style={{
                                    padding: '8px 12px',
                                    backgroundColor: '#EFF6FF',
                                    borderRadius: '6px',
                                    border: '1px solid #BFDBFE',
                                    flex: '1 1 auto',
                                    minWidth: '120px'
                                  }}>
                                    <div style={{
                                      fontFamily: 'Cairo, sans-serif',
                                      fontSize: '11px',
                                      color: '#6B7280',
                                      marginBottom: '4px'
                                    }}>
                                      إجمالي المستلمين
                                    </div>
                                    <div style={{
                                      fontFamily: 'Cairo, sans-serif',
                                      fontSize: '16px',
                                      fontWeight: 700,
                                      color: '#1E40AF'
                                    }}>
                                      {notification.recipientCount}
                                    </div>
                                  </div>
                                  
                                  <div style={{
                                    padding: '8px 12px',
                                    backgroundColor: '#F0FDF4',
                                    borderRadius: '6px',
                                    border: '1px solid #BBF7D0',
                                    flex: '1 1 auto',
                                    minWidth: '120px'
                                  }}>
                                    <div style={{
                                      fontFamily: 'Cairo, sans-serif',
                                      fontSize: '11px',
                                      color: '#6B7280',
                                      marginBottom: '4px'
                                    }}>
                                      من قرأ الإشعار
                                    </div>
                                    <div style={{
                                      fontFamily: 'Cairo, sans-serif',
                                      fontSize: '16px',
                                      fontWeight: 700,
                                      color: '#166534'
                                    }}>
                                      {notification.readCount || 0}
                                    </div>
                                  </div>
                                  
                                  <div style={{
                                    padding: '8px 12px',
                                    backgroundColor: '#FEF2F2',
                                    borderRadius: '6px',
                                    border: '1px solid #FECACA',
                                    flex: '1 1 auto',
                                    minWidth: '120px'
                                  }}>
                                    <div style={{
                                      fontFamily: 'Cairo, sans-serif',
                                      fontSize: '11px',
                                      color: '#6B7280',
                                      marginBottom: '4px'
                                    }}>
                                      من لم يقرأ
                                    </div>
                                    <div style={{
                                      fontFamily: 'Cairo, sans-serif',
                                      fontSize: '16px',
                                      fontWeight: 700,
                                      color: '#991B1B'
                                    }}>
                                      {notification.unreadCount || 0}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                            
                            {/* معلومات المستلم للإشعارات الفردية */}
                            {notification.user && !notification.isGrouped && (
                              <div style={{
                                marginTop: '8px',
                                padding: '6px 10px',
                                backgroundColor: '#F0F9FF',
                                borderRadius: '6px',
                                fontFamily: 'Cairo, sans-serif',
                                fontSize: '12px',
                                color: '#0369A1',
                                display: 'inline-block'
                              }}>
                                <strong>مستلم:</strong> {notification.user.name || notification.user.firstName || 'مستخدم'}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                
                {/* Pagination */}
                {totalPages > 1 && (
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '20px',
                    marginTop: '28px',
                    paddingTop: '24px',
                    borderTop: '2px solid #E5E7EB',
                    flexWrap: 'wrap'
                  }}>
                    <div style={{
                      fontFamily: 'Cairo, sans-serif',
                      fontSize: '15px',
                      color: '#6B7280',
                      fontWeight: 500
                    }}>
                      صفحة {page} من {totalPages} - عرض {paginatedNotifications.length} من {groupedNotifications.length} إشعار مجمع (إجمالي {total} إشعار)
                    </div>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: '10px'
                    }}>
                      <button
                        onClick={() => setPage(1)}
                        disabled={page === 1 || loading}
                        style={{
                          padding: '10px 16px',
                          borderRadius: '10px',
                          border: '1px solid #E5E7EB',
                          backgroundColor: (page === 1 || loading) ? '#F3F4F6' : '#FFFFFF',
                          color: (page === 1 || loading) ? '#9CA3AF' : '#6B7280',
                          fontFamily: 'Cairo, sans-serif',
                          fontSize: '14px',
                          fontWeight: 500,
                          cursor: (page === 1 || loading) ? 'not-allowed' : 'pointer',
                          transition: 'all 0.2s'
                        }}
                      >
                        الأولى
                      </button>
                      <button
                        onClick={() => setPage(prev => Math.max(1, prev - 1))}
                        disabled={page === 1 || loading}
                        style={{
                          padding: '10px 18px',
                          borderRadius: '10px',
                          border: '1px solid #E5E7EB',
                          backgroundColor: (page === 1 || loading) ? '#F3F4F6' : '#FFFFFF',
                          color: (page === 1 || loading) ? '#9CA3AF' : '#6B7280',
                          fontFamily: 'Cairo, sans-serif',
                          fontSize: '14px',
                          fontWeight: 500,
                          cursor: (page === 1 || loading) ? 'not-allowed' : 'pointer',
                          transition: 'all 0.2s'
                        }}
                      >
                        السابق
                      </button>
                      <div style={{
                        display: 'flex',
                        gap: '6px',
                        alignItems: 'center',
                        padding: '0 10px'
                      }}>
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          let pageNum;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (page <= 3) {
                            pageNum = i + 1;
                          } else if (page >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = page - 2 + i;
                          }
                          
                          return (
                            <button
                              key={pageNum}
                              onClick={() => setPage(pageNum)}
                              disabled={loading}
                              style={{
                                padding: '10px 14px',
                                borderRadius: '8px',
                                border: 'none',
                                backgroundColor: page === pageNum ? '#E96A3A' : 'transparent',
                                color: page === pageNum ? '#FFFFFF' : '#6B7280',
                                fontFamily: 'Cairo, sans-serif',
                                fontSize: '14px',
                                fontWeight: page === pageNum ? 600 : 500,
                                cursor: loading ? 'not-allowed' : 'pointer',
                                minWidth: '40px',
                                transition: 'all 0.2s',
                                boxShadow: page === pageNum ? '0px 2px 8px rgba(233, 106, 58, 0.3)' : 'none'
                              }}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                      </div>
                      <button
                        onClick={() => setPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={page === totalPages || loading}
                        style={{
                          padding: '10px 18px',
                          borderRadius: '10px',
                          border: '1px solid #E5E7EB',
                          backgroundColor: (page === totalPages || loading) ? '#F3F4F6' : '#FFFFFF',
                          color: (page === totalPages || loading) ? '#9CA3AF' : '#6B7280',
                          fontFamily: 'Cairo, sans-serif',
                          fontSize: '14px',
                          fontWeight: 500,
                          cursor: (page === totalPages || loading) ? 'not-allowed' : 'pointer',
                          transition: 'all 0.2s'
                        }}
                      >
                        التالي
                      </button>
                      <button
                        onClick={() => setPage(totalPages)}
                        disabled={page === totalPages || loading}
                        style={{
                          padding: '10px 16px',
                          borderRadius: '10px',
                          border: '1px solid #E5E7EB',
                          backgroundColor: (page === totalPages || loading) ? '#F3F4F6' : '#FFFFFF',
                          color: (page === totalPages || loading) ? '#9CA3AF' : '#6B7280',
                          fontFamily: 'Cairo, sans-serif',
                          fontSize: '14px',
                          fontWeight: 500,
                          cursor: (page === totalPages || loading) ? 'not-allowed' : 'pointer',
                          transition: 'all 0.2s'
                        }}
                      >
                        الأخيرة
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        ) : (
          /* Send Notification Form */
          <div style={{ 
            backgroundColor: '#FFFFFF', 
            borderRadius: '20px', 
            padding: '40px',
            maxWidth: '750px',
            margin: '0 auto',
            width: '100%',
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.06)',
            boxSizing: 'border-box',
            border: '1px solid #E5E7EB'
          }}>
            <h3 style={{ 
              fontFamily: 'El Messiri, sans-serif', 
              fontSize: '26px', 
              fontWeight: 700, 
              color: '#1F2937',
              marginBottom: '32px',
              textAlign: 'right',
              background: 'linear-gradient(135deg, #E96A3A 0%, #F27D72 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              إرسال إشعار جديد
            </h3>
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '24px' 
            }}>
              <div>
                <label style={{
                  display: 'block',
                  fontFamily: 'Cairo, sans-serif',
                  fontSize: '15px',
                  fontWeight: 600,
                  color: '#1F2937',
                  marginBottom: '10px',
                  textAlign: 'right'
                }}>
                  عنوان الإشعار
                </label>
                <input
                  type="text"
                  placeholder="أدخل عنوان الإشعار"
                  value={sendForm.title}
                  onChange={(e) => setSendForm({ ...sendForm, title: e.target.value })}
                  disabled={sending}
                  style={{
                    width: '100%',
                    padding: '16px 20px',
                    borderRadius: '14px',
                    border: '2px solid #E5E7EB',
                    fontFamily: 'Cairo, sans-serif',
                    fontSize: '15px',
                    outline: 'none',
                    boxSizing: 'border-box',
                    backgroundColor: sending ? '#F9FAFB' : '#FFFFFF',
                    cursor: sending ? 'not-allowed' : 'text',
                    transition: 'all 0.2s'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#E96A3A'}
                  onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
                  dir="rtl"
                />
              </div>
              
              <div>
                <label style={{
                  display: 'block',
                  fontFamily: 'Cairo, sans-serif',
                  fontSize: '15px',
                  fontWeight: 600,
                  color: '#1F2937',
                  marginBottom: '10px',
                  textAlign: 'right'
                }}>
                  رسالة الإشعار
                </label>
                <textarea
                  placeholder="أدخل رسالة الإشعار"
                  value={sendForm.message}
                  onChange={(e) => setSendForm({ ...sendForm, message: e.target.value })}
                  disabled={sending}
                  style={{
                    width: '100%',
                    padding: '16px 20px',
                    borderRadius: '14px',
                    border: '2px solid #E5E7EB',
                    fontFamily: 'Cairo, sans-serif',
                    fontSize: '15px',
                    outline: 'none',
                    minHeight: '160px',
                    resize: 'vertical',
                    boxSizing: 'border-box',
                    backgroundColor: sending ? '#F9FAFB' : '#FFFFFF',
                    cursor: sending ? 'not-allowed' : 'text',
                    transition: 'all 0.2s'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#E96A3A'}
                  onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
                  dir="rtl"
                />
              </div>
              
              <div>
                <label style={{
                  display: 'block',
                  fontFamily: 'Cairo, sans-serif',
                  fontSize: '15px',
                  fontWeight: 600,
                  color: '#1F2937',
                  marginBottom: '10px',
                  textAlign: 'right'
                }}>
                  المستلمون
                </label>
                <select
                  value={sendForm.target}
                  onChange={(e) => setSendForm({ ...sendForm, target: e.target.value })}
                  disabled={sending}
                  style={{
                    width: '100%',
                    padding: '16px 20px',
                    borderRadius: '14px',
                    border: '2px solid #E5E7EB',
                    fontFamily: 'Cairo, sans-serif',
                    fontSize: '15px',
                    outline: 'none',
                    backgroundColor: sending ? '#F9FAFB' : '#FFFFFF',
                    boxSizing: 'border-box',
                    cursor: sending ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#E96A3A'}
                  onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
                  dir="rtl"
                >
                  <option value="all">جميع المستخدمين</option>
                  <option value="users">المستخدمين فقط</option>
                  <option value="stores">أصحاب المتاجر</option>
                  <option value="kitchens">أصحاب المطابخ</option>
                </select>
              </div>
              
              <button
                onClick={handleSendNotification}
                disabled={sending || !sendForm.title.trim() || !sendForm.message.trim()}
                style={{
                  padding: '16px 40px',
                  borderRadius: '14px',
                  border: 'none',
                  backgroundColor: (sending || !sendForm.title.trim() || !sendForm.message.trim()) 
                    ? '#D1D5DB' 
                    : '#E96A3A',
                  color: '#FFFFFF',
                  fontFamily: 'Cairo, sans-serif',
                  fontSize: '16px',
                  fontWeight: 600,
                  cursor: (sending || !sendForm.title.trim() || !sendForm.message.trim()) 
                    ? 'not-allowed' 
                    : 'pointer',
                  alignSelf: 'flex-end',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  transition: 'all 0.3s',
                  boxShadow: (sending || !sendForm.title.trim() || !sendForm.message.trim())
                    ? 'none'
                    : '0px 6px 20px rgba(233, 106, 58, 0.35)'
                }}
                onMouseEnter={(e) => {
                  if (!sending && sendForm.title.trim() && sendForm.message.trim()) {
                    e.currentTarget.style.transform = 'translateY(-3px)';
                    e.currentTarget.style.boxShadow = '0px 8px 24px rgba(233, 106, 58, 0.45)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = (sending || !sendForm.title.trim() || !sendForm.message.trim())
                    ? 'none'
                    : '0px 6px 20px rgba(233, 106, 58, 0.35)';
                }}
              >
                {sending ? (
                  <>
                    <div style={{
                      width: '20px',
                      height: '20px',
                      border: '3px solid rgba(255, 255, 255, 0.3)',
                      borderTop: '3px solid #FFFFFF',
                      borderRadius: '50%',
                      animation: 'spin 0.6s linear infinite'
                    }}></div>
                    جاري الإرسال...
                  </>
                ) : (
                  'إرسال الإشعار'
                )}
              </button>
            </div>
          </div>
        )}
      </div>
      <ToastContainer />
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.7; transform: scale(1.1); }
        }
        @keyframes slideDown {
          from {
            opacity: 0;
            max-height: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            max-height: 500px;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default الإشعارات;
